import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú) — D.L. 19990, D.L. 25967, D.S. 330-2025-EF
const AÑOS_MINIMOS = 20;
const FACTOR_BASE = 0.35; // 35% a los 20 años de aporte
const INCREMENTO_POR_AÑO = 0.02; // +2% por cada año adicional
const FACTOR_TOPE = 0.80; // tope máximo del 80% de la remuneración de referencia
const PENSION_MINIMA_2026 = 600;
const PENSION_MAXIMA_2026 = 1000;
const TASA_ESSALUD_PENSIONISTA = 0.04;

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraONP() {
  const [añosAporte, setAñosAporte] = useState(25);
  const [remuneracion, setRemuneracion] = useState(2000);

  const resultado = useMemo(() => {
    const años = Math.max(0, Number(añosAporte) || 0);
    const rr = Math.max(0, Number(remuneracion) || 0);

    const cumpleMinimo = años >= AÑOS_MINIMOS;

    if (!cumpleMinimo) {
      return {
        cumpleMinimo,
        años,
        rr,
        factor: 0,
        pensionCalculada: 0,
        pensionAjustada: 0,
        seAplicoMinima: false,
        seAplicoMaxima: false,
        pensionNeta: 0,
      };
    }

    const factor = Math.min(FACTOR_TOPE, FACTOR_BASE + INCREMENTO_POR_AÑO * (años - AÑOS_MINIMOS));
    const pensionCalculada = rr * factor;

    let pensionAjustada = pensionCalculada;
    let seAplicoMinima = false;
    let seAplicoMaxima = false;
    if (pensionCalculada < PENSION_MINIMA_2026) {
      pensionAjustada = PENSION_MINIMA_2026;
      seAplicoMinima = true;
    } else if (pensionCalculada > PENSION_MAXIMA_2026) {
      pensionAjustada = PENSION_MAXIMA_2026;
      seAplicoMaxima = true;
    }

    const pensionNeta = pensionAjustada * (1 - TASA_ESSALUD_PENSIONISTA);

    return {
      cumpleMinimo,
      años,
      rr,
      factor,
      pensionCalculada,
      pensionAjustada,
      seAplicoMinima,
      seAplicoMaxima,
      pensionNeta,
    };
  }, [añosAporte, remuneracion]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            📝
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora ONP
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Pensión del Sistema Nacional de Pensiones (D.L. 19990)</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Años de aporte
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={añosAporte}
                onChange={(e) => setAñosAporte(e.target.value)}
                className={inputClase}
              />
              <p className="mt-1.5 text-xs text-slate-400">
                Se requiere un mínimo de {AÑOS_MINIMOS} años de aporte para acceder a la pensión.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Remuneración de referencia
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={remuneracion}
                  onChange={(e) => setRemuneracion(e.target.value)}
                  className={`${inputClase} pl-9`}
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                Promedio de tus mejores 36 meses dentro de los últimos 60 antes del cese.
              </p>
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            {!resultado.cumpleMinimo ? (
              <>
                <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
                  Pensión estimada
                </p>
                <p className="mb-2 text-2xl font-bold text-amber-400">
                  No cumples el mínimo de {AÑOS_MINIMOS} años
                </p>
                <p className="text-sm text-slate-400">
                  Con {resultado.años} año(s) de aporte no accedes a una pensión de
                  jubilación bajo el D.L. 19990; solo tendrías derecho a la devolución
                  de tus aportes.
                </p>
              </>
            ) : (
              <>
                <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
                  Pensión mensual estimada (neta de EsSalud)
                </p>
                <p className="mb-5 font-mono text-4xl font-bold text-white">
                  {formatearSoles(resultado.pensionNeta)}
                </p>

                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Factor de reemplazo ({resultado.años} años de aporte)</span>
                    <span className="font-mono">{(resultado.factor * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>RR × factor</span>
                    <span className="font-mono">{formatearSoles(resultado.pensionCalculada)}</span>
                  </div>
                  {(resultado.seAplicoMinima || resultado.seAplicoMaxima) && (
                    <div className="flex justify-between text-amber-400">
                      <span>{resultado.seAplicoMinima ? "Pensión mínima aplicada" : "Pensión máxima aplicada"}</span>
                      <span className="font-mono">{formatearSoles(resultado.pensionAjustada)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-400 border-t border-slate-700 pt-1.5 mt-1.5">
                    <span>− EsSalud pensionista (4%)</span>
                    <span className="font-mono">
                      −{formatearSoles(resultado.pensionAjustada * TASA_ESSALUD_PENSIONISTA)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-1 text-xs leading-relaxed text-slate-400">
          <p>
            1. Este cálculo es referencial y no constituye una resolución oficial de
            la ONP.
          </p>
          <p>
            2. La fórmula usada (35% a los 20 años + 2% por año adicional, tope 80%
            de la remuneración de referencia) corresponde al D.L. 19990 y al D.L.
            25967, pero tu resultado real depende del historial de aportes
            verificado por la ONP.
          </p>
          <p>
            3. Pensión mínima S/600 y máxima S/1,000 vigentes desde 2026 (D.S.
            330-2025-EF); ambos montos se actualizan periódicamente.
          </p>
          <p>
            4. Para tu cálculo oficial, consulta gob.pe/onp o llama al (01) 634-2222.
            No constituye asesoría previsional.
          </p>
        </div>
      </div>
    </div>
  );
}